import { useApiQuery } from '@water-web/api';
import { CartModel } from '@water-web/repository';

import { isUserInfoValid } from '../../types';
import { CustomerServiceConstructor } from '../../apiServices';
import { useAppSettingsContext } from '../../contexts';
import { Logger } from '../../observability';

interface Result {
  bookAndPay: boolean;
  bookNoPay: boolean;
  bookNoPayAndProvideCard: boolean;
  isLoading: boolean;
  isPrepayOnlyCustomer: boolean;
}

export const useAvailableBookingTypes = (cartModel: CartModel): Result => {
  const appSettingsConfig = useAppSettingsContext();
  const customerService = CustomerServiceConstructor(appSettingsConfig.apiUrl);

  const customer = cartModel.getCustomer()?.getDataValues();
  const cart = cartModel.getValidatedCart()?.getDataValues();
  const { canReserve, canBookNoPay } = cart?.breakdown ?? {};

  const { data: isPrepayOnlyCustomer, isLoading } = useApiQuery({
    endpoint: customerService.prepayOnlyByMatch,
    payload: {
      shopId: cart.shopId,
      barberId: cart.new_appointments[0]?.barberId ?? undefined, // FIXME: check all appointments
      customer,
    },
    options: {
      initialData: false,
      enabled: canBookNoPay && isUserInfoValid(customer),
    },
  });

  // Log when customer is marked as prepay-only despite shop allowing pay-in-person
  try {
    const shopAllowsInPersonPayments = cart?.shop?.allowInPersonPayments;
    const shopRequiresPrepayment = cart?.shop?.requiresPrepayment;

    if (isPrepayOnlyCustomer && shopAllowsInPersonPayments && !shopRequiresPrepayment && canBookNoPay) {
      Logger.error('[BT-10271] API-RESPONSE: Customer flagged as prepay-only in useAvailableBookingTypes', {
        // API endpoint details
        apiEndpoint: 'customerService.prepayOnlyByMatch',
        prepayOnlyResult: isPrepayOnlyCustomer,

        // Shop information
        shop: {
          id: cart?.shopId,
          name: cart?.shop?.name,
          route: cart?.shop?.route,
          brandId: cart?.shop?.brandId,
          settings: {
            allowInPersonPayments: shopAllowsInPersonPayments,
            requiresPrepayment: shopRequiresPrepayment,
            requestCardForInPersonPayments: cart?.shop?.requestCardForInPersonPayments,
            bookNoPay: cart?.shop?.bookNoPay,
          },
        },

        // Customer information (what was sent to API)
        customer: {
          id: customer?.id,
          email: customer?.email,
          phone: customer?.phone,
          firstName: customer?.firstName,
          lastName: customer?.lastName,
        },

        // Barber information (sent to prepayOnly API)
        barber: {
          id: cart?.new_appointments?.[0]?.barberId,
          // API checks barber-specific prepay rules
        },

        // Cart and payment details
        cart: {
          id: cart?.id,
          breakdown: {
            canBookNoPay,
            canReserve,
            outstandingChargeAmount: cart?.breakdown?.outstandingChargeAmount,
            amountLeftForPayment: cart?.breakdown?.amountLeftForPayment,
            totalChargeAmount: cart?.breakdown?.totalChargeAmount,
          },
        },

        // Appointment details for DB lookup
        appointments:
          cart?.new_appointments?.map((apt) => ({
            barberId: apt.barberId,
            customerId: apt.customerId,
            dateTime: apt.dateTime,
            services: apt.services?.map((s) => s.id) || [],
          })) ?? [],

        // Client environment for correlation
        clientInfo: {
          userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
          timestamp: new Date().toISOString(),
          referrer: typeof window !== 'undefined' ? document.referrer : null,
          url: typeof window !== 'undefined' ? window.location.href : null,
        },

        // Source file identification
        sourceFile: 'packages/view/src/hooks/useAvailableBookingTypes/useAvailableBookingTypes.ts',
      });
    }
  } catch (loggingError) {
    // do nothing
  }

  return {
    bookAndPay: true, // always allowed
    bookNoPay: (!isPrepayOnlyCustomer && canBookNoPay) || false,
    bookNoPayAndProvideCard: (!isPrepayOnlyCustomer && canReserve) || false,
    isLoading,
    isPrepayOnlyCustomer,
  };
};
